import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import { convertCommission, getCommission } from '../../services/HydraService';

function formatUsd(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '0,00';
    return number.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

function formatQty(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '0';
    return number.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8
    });
}

function formatWhen(value) {
    if (!value) return '—';
    return new Date(value).toLocaleString('pt-BR');
}

function apiError(err) {
    const raw = err?.response ? err.response.data : err?.message;
    if (typeof raw === 'string') return raw;
    if (raw && raw.message) return raw.message;
    return raw ? JSON.stringify(raw) : 'Não foi possível carregar a comissão.';
}

function MoneyCard({ label, value, suffix, hint }) {
    return (
        <div className="col-12 col-md-6 col-xl-3 mb-4">
            <div className="card border-0 shadow h-100">
                <div className="card-body">
                    <small className="text-muted">{label}</small>
                    <h3 className="fw-extrabold mb-1">{formatUsd(value)} {suffix}</h3>
                    <span className="text-muted">{hint}</span>
                </div>
            </div>
        </div>
    );
}

function Commission() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [converting, setConverting] = useState(false);
    const [notification, setNotification] = useState({ text: '', type: '' });

    function load(showSpinner = true) {
        if (showSpinner) setLoading(true);
        return getCommission()
            .then(result => setData(result))
            .catch(err => setNotification({ type: 'error', text: apiError(err) }))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        load();
    }, []);

    async function onConvert() {
        setConverting(true);
        try {
            const result = await convertCommission();
            if (result?.summary)
                setData(result.summary);
            else
                await load(false);
            setNotification({ type: 'success', text: 'USDT da comissão convertido em BTC.' });
        } catch (err) {
            setNotification({ type: 'error', text: apiError(err) });
        } finally {
            setConverting(false);
        }
    }

    const rate = Math.round((Number(data?.rate) || 0.1) * 100);
    const entries = data?.entries || [];
    const conversions = data?.conversions || [];
    const funds = data?.funds?.users || [];
    const commissionPending = Number(data?.usdcPending) || 0;
    const fundPending = Number(data?.funds?.usdcPending) || 0;
    const waitingUsdc = commissionPending + fundPending;

    return (
        <>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <h2 className="h4">Comissão do app</h2>
                        <p className="mb-0 text-muted">
                            De cada trade com lucro, {rate}% vai para o app e {rate}% fica no fundo do cliente. A conta é em cima do lucro, não da carteira.
                        </p>
                    </div>
                    <div className="btn-toolbar mb-2 mb-md-0">
                        <button type="button" className="btn btn-sm btn-outline-gray-600 me-2" onClick={() => load()} disabled={loading}>
                            {loading ? 'Atualizando...' : 'Atualizar'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-gray-800"
                            onClick={onConvert}
                            disabled={converting || !data?.canBuyBtc}
                        >
                            {converting ? 'Comprando BTC...' : 'Comprar BTC com a comissão'}
                        </button>
                    </div>
                </div>

                {loading && !data
                    ? <p className="text-muted">Carregando comissão...</p>
                    : null}

                {data && (
                    <>
                        <div className="card border-0 shadow mb-4">
                            <div className="card-body">
                                <h3 className="h5">Como o dinheiro anda</h3>
                                <ol className="mb-0">
                                    <li>O cliente lucra em USDC. Tiramos {rate}% desse lucro para o app e {rate}% para o fundo dele.</li>
                                    <li>Esse USDC espera junto até atingir o mínimo da Binance e só então vira USDT.</li>
                                    <li>O USDT do app pode ser comprado em BTC. O USDT do fundo do cliente fica parado e não vira BTC.</li>
                                </ol>
                            </div>
                        </div>

                        {waitingUsdc > 0 && (
                            <div className="alert alert-warning">
                                Ainda há {formatUsd(waitingUsdc)} USDC na fila da troca.
                                {' '}{formatUsd(commissionPending)} são da comissão do app e {formatUsd(fundPending)} são do fundo dos clientes.
                                A troca para USDT só acontece quando o lote chega no mínimo da Binance.
                            </div>
                        )}

                        <h3 className="h5">Comissão do app</h3>
                        <p className="text-muted">Estes valores são seus. Podem virar BTC.</p>
                        <div className="row">
                            <MoneyCard
                                label="USDT pronto para BTC"
                                value={data.usdtAvailable}
                                suffix="USDT"
                                hint={data.canBuyBtc
                                    ? `Já dá para comprar ${data.btcSymbol || 'BTC'}.`
                                    : `Faltam ${formatUsd(data.usdtToNextBtc)} USDT. O mínimo é ${formatUsd(data.minNotional)}.`}
                            />
                            <MoneyCard
                                label="USDT que já virou BTC"
                                value={data.usdtSpentOnBtc}
                                suffix="USDT"
                                hint="Soma do que já foi gasto na compra de BTC."
                            />
                            <div className="col-12 col-md-6 col-xl-3 mb-4">
                                <div className="card border-0 shadow h-100">
                                    <div className="card-body">
                                        <small className="text-muted">BTC comprado</small>
                                        <h3 className="fw-extrabold mb-1">{formatQty(data.btcAmount)} BTC</h3>
                                        <span className="text-muted">Fica na comissão. Não entra no fundo do cliente.</span>
                                    </div>
                                </div>
                            </div>
                            <MoneyCard
                                label="Comissão tirada hoje"
                                value={data.today?.collectedUsdc}
                                suffix="USDC"
                                hint={`${data.today?.count || 0} trade${(data.today?.count || 0) === 1 ? '' : 's'} com lucro. Ainda pode estar na fila do USDT.`}
                            />
                        </div>

                        <h3 className="h5">Fundo dos clientes</h3>
                        <p className="text-muted">Estes valores são dos clientes. Ficam em USDT e não viram BTC.</p>
                        <div className="row">
                            <MoneyCard
                                label="USDT guardado"
                                value={data.funds?.usdtAvailable}
                                suffix="USDT"
                                hint={`${data.funds?.count || 0} cliente${(data.funds?.count || 0) === 1 ? '' : 's'}. Separado do seu USDC.`}
                            />
                            <MoneyCard
                                label="Ainda em USDC"
                                value={fundPending}
                                suffix="USDC"
                                hint="Parte do fundo que ainda não virou USDT."
                            />
                            <MoneyCard
                                label="Fundo tirado hoje"
                                value={data.today?.reservedUsdc}
                                suffix="USDC"
                                hint="10% do lucro de hoje que foi para o fundo dos clientes."
                            />
                        </div>

                        <div className="card border-0 shadow mb-4">
                            <div className="card-body">
                                <h3 className="h5">Trades cobrados</h3>
                                <p className="text-muted small mb-3">
                                    Cada linha é um lucro. A comissão e o fundo são {rate}% desse lucro, não da carteira.
                                </p>
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle">
                                        <thead>
                                            <tr>
                                                <th>Quando</th>
                                                <th>Cliente</th>
                                                <th>Par</th>
                                                <th className="text-end">Lucro do trade</th>
                                                <th className="text-end">{rate}% comissão do app</th>
                                                <th className="text-end">{rate}% fundo do cliente</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {entries.length
                                                ? entries.map(row => (
                                                    <tr key={row.id}>
                                                        <td>{formatWhen(row.createdAt)}</td>
                                                        <td>
                                                            {row.userId
                                                                ? <Link to={`/users/${row.userId}`}>{row.userName || row.userEmail || `#${row.userId}`}</Link>
                                                                : '—'}
                                                            {row.userEmail
                                                                ? <div className="small text-muted">{row.userEmail}</div>
                                                                : null}
                                                        </td>
                                                        <td>{row.symbol}</td>
                                                        <td className="text-end">{formatUsd(row.profit)}</td>
                                                        <td className="text-end">{formatUsd(row.collectedUsdc)}</td>
                                                        <td className="text-end">{formatUsd(row.reservedUsdc)}</td>
                                                    </tr>
                                                ))
                                                : (
                                                    <tr>
                                                        <td colSpan="6" className="text-muted">Nenhum lucro cobrado ainda.</td>
                                                    </tr>
                                                )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="card border-0 shadow mb-4">
                            <div className="card-body">
                                <h3 className="h5">Fundo por cliente</h3>
                                <p className="text-muted small mb-3">
                                    Só você vê isto. O cliente não opera esse USDT e ele não entra na compra de BTC.
                                </p>
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle">
                                        <thead>
                                            <tr>
                                                <th>Cliente</th>
                                                <th className="text-end">USDT no fundo</th>
                                                <th className="text-end">USDT acumulado</th>
                                                <th className="text-end">USDC esperando troca</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {funds.length
                                                ? funds.map(row => (
                                                    <tr key={row.userId}>
                                                        <td>
                                                            {row.userId
                                                                ? <Link to={`/users/${row.userId}`}>{row.userName || row.userEmail || `#${row.userId}`}</Link>
                                                                : '—'}
                                                            {row.userEmail
                                                                ? <div className="small text-muted">{row.userEmail}</div>
                                                                : null}
                                                        </td>
                                                        <td className="text-end">{formatUsd(row.usdtAvailable)}</td>
                                                        <td className="text-end">{formatUsd(row.usdtTotal)}</td>
                                                        <td className="text-end">{formatUsd(row.usdcPending)}</td>
                                                    </tr>
                                                ))
                                                : (
                                                    <tr>
                                                        <td colSpan="4" className="text-muted">Nenhum fundo ainda.</td>
                                                    </tr>
                                                )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="card border-0 shadow mb-4">
                            <div className="card-body">
                                <h3 className="h5">Compras de BTC</h3>
                                <p className="text-muted small mb-3">Só a comissão do app entra aqui. O fundo do cliente não é gasto.</p>
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle">
                                        <thead>
                                            <tr>
                                                <th>Quando</th>
                                                <th>Par</th>
                                                <th className="text-end">USDT gasto</th>
                                                <th className="text-end">BTC recebido</th>
                                                <th className="text-end">Preço</th>
                                                <th>Ordem</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {conversions.length
                                                ? conversions.map(row => (
                                                    <tr key={row.id}>
                                                        <td>{formatWhen(row.createdAt)}</td>
                                                        <td>{row.symbol}</td>
                                                        <td className="text-end">{formatUsd(row.usdcSpent)}</td>
                                                        <td className="text-end">{formatQty(row.btcQty)}</td>
                                                        <td className="text-end">{formatUsd(row.price)}</td>
                                                        <td>{row.orderId || '—'}</td>
                                                    </tr>
                                                ))
                                                : (
                                                    <tr>
                                                        <td colSpan="6" className="text-muted">A comissão ainda não comprou BTC.</td>
                                                    </tr>
                                                )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </>
                )}
                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
        </>
    );
}

export default Commission;
